import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { ASSOCIATE_GRID_COLUMNS } from '../../constants/personAssociateFormConstants';
import PersonAssociateDialog from './PersonAssociateDialog';
import { getPeopleAssociates, deletePersonAssociate } from '../../services/userService';

const PersonAssociatesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAssociate, setEditingAssociate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;

  const loadAssociates = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleAssociates(personId),
      onSuccess: (data) => setAssociates(data),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadAssociates();
  }, [loadAssociates]);

  const handleAdd = useCallback(() => {
    setEditingAssociate(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingAssociate(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingAssociate(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadAssociates();
  }, [handleFormClose, loadAssociates]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${row.FullName || 'associate'}" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () =>
              deletePersonAssociate({
                ptsPersonID: personId,
                ptsAssociateID: row.ptsAssociateID,
              }),
            onSuccess: () => {
              dispatch(notify('Associate deleted successfully', 'success'));
              loadAssociates();
            },
          });
        },
        'Delete Associate'
      );
    },
    [requestConfirm, dispatch, personId, loadAssociates]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Associate"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={associates}
        columns={ASSOCIATE_GRID_COLUMNS}
        getRowId={(row) => row.ptsAssociateID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Person Associates',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonAssociateDialog
          open={formOpen}
          personId={personId}
          associate={editingAssociate}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonAssociatesTab;
